// Minnesotaänger Mini App — middle column header.
// Restored to telegram-tt's MiddleHeader DOM contract so the baseline
// (0df85b8d classic) MiddleHeader.scss applies: `.MiddleHeader`
// (full-width in-flow bar) with `.back-button` (round translucent Button
// containing the animated back icon), `.chat-info-wrapper` > `.ChatInfo` >
// Avatar + `.info` > `.title` > `h3.fullName` (v1 shows the conversation
// title; telegram-tt also rendered online/typing status, search and
// pinned-messages tools here).
// Removed: Transition wrapper, HeaderActions, typing status. The back
// button is always rendered (v1 has no right column / master-detail
// desktop split to fall back on).

import { memo } from '../../lib/teact/teact';

import { getActions, withGlobal } from '../../global';

import type { ApiMiniAppConversation } from '../../api/miniapp/types';

import { selectActiveChat } from '../../global/selectors/miniapp';
import { getAvatarLabel, getDisplayName } from '../../global/helpers/displayNames';
import useLastCallback from '../../hooks/useLastCallback';
import { handleMobileBack } from '../../util/backNavigation';
import buildClassName from '../../util/buildClassName';

import Avatar from '../common/Avatar';
import Button from '../ui/Button';

import './MiddleHeader.scss';

type OwnProps = {
  chatId: string;
  isMobile?: boolean;
};

type StateProps = {
  conversation?: ApiMiniAppConversation;
};

function MiddleHeader({ chatId, conversation, isMobile }: OwnProps & StateProps) {
  const { openPreviousChat } = getActions();

  // conversation.title is the raw assistant_id ("gilbert"); v1 renders the
  // display name, falling back to the app name when no chat is open.
  const displayName = conversation?.title ? getDisplayName(conversation.title) : 'Minnesänger';

  const handleBackClick = useLastCallback(() => {
    if (isMobile) {
      // telegram-tt blurs the composer so the keyboard closes before the
      // column slides back. Order matters — extracted to util/backNavigation
      // so it is unit-tested (tests/contract/mobile-back.test.ts).
      handleMobileBack(openPreviousChat);
    } else {
      openPreviousChat();
    }
  });

  return (
    // v1 (fix-list C): `mobile-shell` restructures the header into an iOS
    // nav bar (44pt back hit target, centered title, "bot" subtitle, 36pt
    // right avatar, translucent dark + backdrop blur). Desktop JSX is
    // unchanged — only the in-`.ChatInfo` avatar is hidden on mobile.
    <div className={buildClassName('MiddleHeader', isMobile && 'mobile-shell')}>
      <div className="back-button">
        <Button
          round
          size="smaller"
          color="translucent"
          onClick={handleBackClick}
          ariaLabel="返回"
        >
          <div className="animated-close-icon state-back" />
        </Button>
      </div>
      <div className="chat-info-wrapper">
        <div className="ChatInfo">
          {!isMobile && <Avatar name={getAvatarLabel(conversation?.title ?? '')} chatId={chatId} size="medium" />}
          <div className="info">
            <div className="title">
              <h3 className="fullName" dir="auto">{displayName}</h3>
            </div>
            {isMobile && <div className="status">bot</div>}
          </div>
        </div>
      </div>
      {isMobile && (
        <div className="header-avatar">
          <Avatar name={getAvatarLabel(conversation?.title ?? '')} chatId={chatId} size="medium" />
        </div>
      )}
      <div className="header-tools" />
    </div>
  );
}

export default memo(withGlobal<OwnProps>((global): StateProps => ({
  conversation: selectActiveChat(global),
}))(MiddleHeader));
